import { useMemo } from "react";
import { Reveal, MaskedTitle } from "@/components/motion";
import { ARTICLE_BY_SLUG, ARTICLES } from "@/data/articles";

const Block = ({ block, first }) => {
  switch (block.type) {
    case "lead":
      return <p className="article-lead" data-testid="article-lead">{block.text}</p>;
    case "h3":
      return <h3 className="article-h3">{block.text}</h3>;
    case "quote":
      return (
        <blockquote className="article-quote">
          <p>{block.text}</p>
          {block.cite ? <cite>— {block.cite}</cite> : null}
        </blockquote>
      );
    case "ul":
      return (
        <ul className="article-list">
          {block.items.map((it, i) => <li key={i}>{it}</li>)}
        </ul>
      );
    case "ol":
      return (
        <ol className="article-numlist">
          {block.items.map((it, i) => (
            <li key={i}>
              <strong>{it.title}</strong>
              <span>{it.desc}</span>
            </li>
          ))}
        </ol>
      );
    case "cta-pair":
      return (
        <div className="article-cta-pair" data-testid="article-cta-pair">
          <a href={block.primary.href} target={block.primary.href.startsWith("#") ? undefined : "_blank"} rel="noopener noreferrer" className="btn btn-primary">{block.primary.label}</a>
          {block.secondary ? (
            <a href={block.secondary.href} target={block.secondary.href.startsWith("#") ? undefined : "_blank"} rel="noopener noreferrer" className="btn btn-ghost btn-ghost-light">{block.secondary.label}</a>
          ) : null}
        </div>
      );
    case "p":
    default:
      return <p className={first ? "article-p article-p-first" : "article-p"}>{block.text}</p>;
  }
};

export const ArticlePage = ({ slug }) => {
  const article = ARTICLE_BY_SLUG[slug];

  const related = useMemo(() => {
    if (!article) return [];
    return ARTICLES.filter((a) => a.slug !== article.slug && a.category === article.category).slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="article-notfound">
        <section className="hero-dark"><div className="hero-motif" /><div className="container">
          <div className="hero-copy" style={{ padding: "80px 0" }}>
            <span className="eyebrow on-dark">404</span>
            <h1 className="display display-l">Article not found.</h1>
            <p className="lede on-dark">The article you're looking for doesn't exist. It may have moved.</p>
            <a href="/resources" className="btn btn-primary" data-testid="article-back-cta">Back to Resources</a>
          </div>
        </div></section>
      </div>
    );
  }

  const firstBodyIdx = article.body.findIndex((b) => b.type === "p" || b.type === "lead");

  return (
    <>
      <section className="hero-dark article-hero">
        <div className="hero-motif" />
        <div className="hero-glow" aria-hidden="true" />
        <div className="container">
          <a href="/resources" className="article-back" data-testid="article-back-link">← All resources</a>
          <div className="article-hero-grid article-hero-grid--text">
            <div className="article-hero-copy">
              <span className="eyebrow on-dark" data-testid="article-category">{article.category}</span>
              <MaskedTitle text={article.title} className="display display-xl article-title" testid="article-title" />
              {article.subtitle ? <p className="lede on-dark article-subtitle" data-testid="article-subtitle">{article.subtitle}</p> : null}
              <div className="article-meta" data-testid="article-meta">
                {article.author ? <span className="article-author">{article.author}</span> : null}
                {article.author ? <span className="article-meta-sep">·</span> : null}
                <span className="article-date">{article.dateline || article.date}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section article-section">
        <Reveal className="container article-container">
          <article className="article-prose" data-testid="article-prose">
            {article.body.map((block, i) => (
              <Block key={i} block={block} first={i === firstBodyIdx} />
            ))}
          </article>
        </Reveal>
      </section>

      {related.length > 0 ? (
        <section className="section-tight article-related section-alt">
          <div className="container">
            <div className="sec-head" style={{ marginBottom: 28 }}>
              <div>
                <span className="eyebrow">Keep Reading</span>
                <h2 className="display display-s mt-6">More in {article.category}.</h2>
              </div>
              <a href="/resources" className="post-arrow">All resources →</a>
            </div>
            <div className="post-list" data-testid="article-related-list">
              {related.map((r) => (
                <a key={r.slug} href={`/resources/${r.slug}`} className="post-row" data-testid={`related-${r.slug}`}>
                  <span className="tag-chip tag-related">{r.category}</span>
                  <div className="post-body">
                    <h4>{r.title}</h4>
                    <p>{r.subtitle}</p>
                  </div>
                  <span className="post-arrow">Read →</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
};
